import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Icon from '@mui/material/Icon';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';
import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';

import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import {
    get as _get,
    isEqual as _isEqual,
} from 'lodash';

import SearchBar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import UsersService, { userFields } from '../../../data/users-service';
import RolesService, { roleFields } from '../../../data/roles-service';
import Auth, { ProfileContext, User } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';
import DashboardService from '../../../data/dashboard-service';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
}));

const Users = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const user = User().username;
    const [userRoleTab, setUserRoleTab] = useState(0);
    const [userData, setUserData] = useState([]);
    const [usersCanDelete, setUsersCanDelete] = useState({});
    const [roleData, setRoleData] = useState([]);
    const [userSearch, setUserSearch] = useState();
    const [loading,setLoading]=useState(true);
    const [roleSearch, setRoleSearch] = useState();
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [deleteRoleDialog, setDeleteRoleDialog] = useState(false);
    const [passwordResetDialog, setPasswordResetDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const usersPerms = Auth.permissions.get('Users');
    const rolesPerms = Auth.permissions.get('Roles');
    const { height } = useWindowSize();
    const userTableAPIRef = useApiRef();
    const roleTableAPIRef = useApiRef();
    const [userColumnWidth, setUserColumnWidth] = useState({});
    const [roleColumnWidth, setRoleColumnWidth] = useState({});
    const [userSortModel, setUserSortModel] = useState([{
        field: 'username',
        sort: 'asc',
    }]);
    const [roleSortModel, setRoleSortModel] = useState([{
        field: 'name',
        sort: 'asc',
    }]);
    const [txfDashboards, setTxfDashboards] = useState(true);

    const handleUpdateUserSort = s => {
        if (!_isEqual(s, userSortModel)) {
            setUserSortModel(s);
        }
    };

    const handleUpdateRoleSort = s => {
        if (!_isEqual(s, roleSortModel)) {
            setRoleSortModel(s);
        }
    };

    useEffect(() => {
        getUsers();
        getRoles();

        if (location.state && location.state.tab) {
            setUserRoleTab(location.state.tab);
        }

        setUserColumnWidth(getProfile('usersroles').userWidths || {});
        setRoleColumnWidth(getProfile('usersroles').roleWidths || {});
    }, [getProfile, location.state]);

    useEffect(() => {
        getUsers(userSearch);
    }, [userSearch]);

    useEffect(() => {
        getRoles(roleSearch);
    }, [roleSearch]);

    useEffect(() => {
        userData.forEach(u => {
            UsersService.havePerms(u.username).then(res => {
                setUsersCanDelete(current => {
                    return {
                        ...current,
                        [u.username]: res.havePerms,
                    };
                });
            });
        });
    }, [userData]);

    const getUsers = (q) => {
        UsersService.getUsers(q).then(res => {
            const u = res.data.map((v) => ({ ...v, id: Helpers.UniqueID() }));
            setUserData(u);
            setLoading(false);
        }, err => {
            console.error('Unable to get users ::: ', err);
        });
    };

    const getRoles = (q) => {
        RolesService.getRoles(q).then(res => {
            const r = res.data.map((v) => ({ ...v, id: Helpers.UniqueID() }));
            setRoleData(r);
            setLoading(false);
        }, err => {
            console.error('Unable to get roles ::: ', err);
        });
    };

    const handleRoleColumnResize = params => {
        const widths = roleTableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('usersroles', {
            ...getProfile('usersroles'),
            userWidths: widths,
        });
        setRoleColumnWidth(widths);
    };

    const handleRoleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = roleTableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('usersroles', {
                ...getProfile('usersroles'),
                roleColumns: order,
            });
        });
    };

    const handleUserColumnResize = params => {
        const widths = userTableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('usersroles', {
            ...getProfile('usersroles'),
            roleWidths: widths,
        });
        setUserColumnWidth(widths);
    };

    const handleUserColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = userTableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('usersroles', {
                ...getProfile('usersroles'),
                userColumns: order,
            });
        });
    };

    const handleCreateUser = () => {
        navigate('/system/users_roles/create_user', { state: { fromUsers: true } });
    };

    const handleCreateRole = () => {
        navigate('/system/users_roles/create_role');
    };

    const handleUserSearchChange = ({ search }) => {
        setUserSearch(search);
    };

    const handleRoleSearchChange = ({ search }) => {
        setRoleSearch(search);
    };

    const tableRenderers = {
        name: d => (<div>{d.row.name}</div>),
        username: d => (<div>{d.row.username}</div>),
        email: d => (<div>{d.row.email}</div>),
        access: d => (<div>{d.row.access}</div>),
        roles: d => {
            if (_get(d, 'row.type') !== 'brc') {
                return d.row.type === 'ldap'
                    ? <div style={{ display: 'flex', alignUItems: 'center' }}>
                        Ldap Authorization
                    </div>
                    : d.row.type === 'saml' ? 'Saml Authorization'
                        : d.row.type === 'okta' ? 'Okta Authorization'
                            : d.row.roles;
            } else {
                return (
                    <React.Fragment>
                        {d.row.role_sample.map((v, i) => {
                            return (
                                <div className={classes.roleUsers} key={'user-roles-' + i}>
                                    {v}{i !== d.row.role_sample.length - 1 ? ', ' : ''}
                                </div>
                            );
                        })}
                        {(() => {
                            const n = d.row.role_count - d.row.role_sample.length;
                            return (n ? <div className={classes.roleUsers}>
                                {` and ${n} ${n === 1 ? 'other' : 'others'}`}
                            </div> : null);
                        })()}
                    </React.Fragment>
                );
            }
        },
        description: d => (<div>{d.row.description}</div>),
        users: d => {
            return (
                <React.Fragment>
                    {d.row.user_sample.map((v, i) => {
                        return (
                            <div className={classes.roleUsers} key={'role-users-' + i}>
                                {v}{i !== d.row.user_sample.length - 1 ? ', ' : ''}
                            </div>
                        );
                    })}
                    {(() => {
                        const n = d.row.user_count - d.row.user_sample.length;
                        return (n ? <div className={classes.roleUsers}>
                            {` and ${n} ${n === 1 ? 'other' : 'others'}`}
                        </div> : null);
                    })()}
                </React.Fragment>
            );
        },
    };

    const userColumns = userFields.map(f => {
        return {
            ...f,
            width: userColumnWidth[f.headerName] || 0,
            ...(!userColumnWidth[f.headerName] ? { flex: 1 } : {}),
            disableColumnMenu: true,
            field: f.variable,
            renderCell: tableRenderers[f.variable],
        };
    });

    const roleColumns = roleFields.map(f => {
        return {
            ...f,
            width: roleColumnWidth[f.headerName] || 0,
            ...(!roleColumnWidth[f.headerName] ? { flex: 1 } : {}),
            disableColumnMenu: true,
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleSelectUser = (s) => {
        setSelectedUser(s.row);
    };

    const handleSelectRole = (s) => {
        setSelectedRole(s.row);
    };

    const handleEditUser = () => {
        navigate(`/system/users_roles/update_user/${selectedUser.username}`, { state: { fromUsers: true } });
    };

    const handleEditRole = () => {
        navigate(`/system/users_roles/update_role/${selectedRole.name}`);
    };

    const handleCloseDeleteUserDialog = () => {
        setDeleteUserDialog(false);
        setTimeout(() => {
            setSelectedUser(null);
        }, 1000);
    };

    const handleCloseDeleteRoleDialog = () => {
        setDeleteRoleDialog(false);
        setTimeout(() => {
            setSelectedRole(null);
        }, 1000);
    };

    const handleOpenDeleteUser = () => {
        setDeleteUserDialog(true);
    };

    const handleOpenDeleteRole = () => {
        setDeleteRoleDialog(true);
    };

    const handleDeleteUser = (e, skipTxfr) => {
        if (!skipTxfr && txfDashboards) {
            // will delete after
            handleTxfDashboards();
            return;
        }
        UsersService.deleteUser(selectedUser.username).then(() => {
            getUsers(userSearch);
            toast.success('User deleted successfully');
        }, err => {
            console.error('Error deleting user ::: ', err);
            toast.error('Error deleting user');
        }).finally(() => {
            setSelectedUser(null);
            setDeleteUserDialog();
        });
    };

    const handleTxfDashboards = () => {
        DashboardService.txfr(selectedUser.username, user).then(() => {
            toast.success('Dashboards transferred successfully');
        }).catch(() => {
            toast.error('Error transferring dashboards');
        }).finally(() => {
            // delete anyway
            handleDeleteUser(0, true);
        });
    };

    const handleDeleteRole = () => {
        RolesService.deleteRole(selectedRole.name).then(() => {
            getRoles();
            toast.success('Role deleted successfully');
        });
        setSelectedRole(null);
        setDeleteRoleDialog(false);
    };

    const handleClosePasswordResetDialog = () => {
        setPasswordResetDialog(false);
        setTimeout(() => {
            setSelectedRole(null);
        }, 1000);
    };

    const handlePasswordReset = (e) => {
        toast.info('Sending reset password email to user...');
        UsersService.resetPassword(selectedUser.username);
        setPasswordResetDialog(false);
    };

    const handleOpenResetPassword = () => {
        setPasswordResetDialog(true);
    };

    const handleSetPassword = (e) => {
        navigate(`/system/users_roles/password/${selectedUser.username}`);
    };

    const handleTabChange = (e, tab) => {
        setUserRoleTab(tab);
    };

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {userRoleTab === 0 && <Box className={classes.fullWidth}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar immediate onSearch={handleUserSearchChange} searchservice={UsersService}/>
                            {usersPerms.write && <Box>
                                <Button
                                    variant="contained"
                                    className={classes.buttonRight}
                                    color="primary"
                                    aria-label="add user"
                                    onClick={handleCreateUser}
                                >
                                    <AddIcon/> Create User
                                </Button>
                            </Box>}
                        </Box>
                    </Box>}
                    {userRoleTab === 1 && <Box className={classes.fullWidth}>
                        <Box display="flex" justifyContent="space-between">
                            <SearchBar immediate onSearch={handleRoleSearchChange} searchservice={RolesService}/>
                            {rolesPerms.write && <Box>
                                <Button
                                    variant="contained"
                                    className={classes.buttonRight}
                                    color="primary"
                                    aria-label="add role"
                                    onClick={handleCreateRole}
                                >
                                    <AddIcon/> Create Role
                                </Button>
                            </Box>}
                        </Box>
                    </Box>}
                    <AppBar position="static" className={classes.tabBar}>
                        <Tabs value={userRoleTab} onChange={handleTabChange}>
                            <Tab aria-label="Users" label="Users" value={0}/>
                            <Tab aria-label="Roles" label="Roles" value={1}/>
                        </Tabs>
                    </AppBar>
                    {userRoleTab === 0 && <Box value={userRoleTab} index={0} height={height - 310} width="100%">
                        <DataGridPro
                            apiRef={userTableAPIRef}
                            components={{
                                Header: !(usersPerms.write || usersPerms.delete || usersPerms.admin || usersPerms.override) ? undefined : () => {
                                    return (
                                        <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                            {usersPerms.override && <IconButton
                                                disabled={!selectedUser || (selectedUser?.username === 'admin' && user !== 'admin')}
                                                variant="contained"
                                                name="set password"
                                                aria-label="set password"
                                                title="set password"
                                                onClick={handleSetPassword}
                                                size="large"><LockIcon/></IconButton>}
                                            {usersPerms.admin && <IconButton
                                                disabled={!selectedUser || (selectedUser?.username === 'admin' && user !== 'admin')}
                                                variant="contained"
                                                name="reset password"
                                                title="reset password"
                                                aria-label="reset password"
                                                onClick={handleOpenResetPassword}
                                                size="large"
                                            >
                                                {(() => {
                                                    if (!selectedUser) {
                                                        return (<Icon><img height="24px" src="/images/reset-icon-disabled.png"/></Icon>);
                                                    } else if (theme.palette.mode === 'dark') {
                                                        return (<Icon><img height="24px" src="/images/reset-icon-white.png"/></Icon>);
                                                    }
                                                    return (<Icon><img height="24px" src="/images/reset-icon-black.png"/></Icon>);
                                                })()}
                                            </IconButton>}
                                            {usersPerms.write && <IconButton
                                                disabled={!selectedUser}
                                                variant="contained"
                                                color="primary"
                                                name="edit user"
                                                aria-label="edit user"
                                                title="edit user"
                                                onClick={handleEditUser}
                                                size="large"><EditIcon/></IconButton>}
                                            {usersPerms.delete &&
                                              <IconButton
                                                  disabled={!selectedUser ||
                                                    selectedUser.username === user ||
                                                    selectedUser.username === 'admin' ||
                                                    !usersCanDelete[selectedUser.username]
                                                  }
                                                  variant="contained"
                                                  color="secondary"
                                                  name="delete user"
                                                  title="delete user"
                                                  aria-label="delete user"
                                                  onClick={handleOpenDeleteUser}
                                                  size="large">
                                                  <DeleteIcon/>
                                              </IconButton>}
                                        </Box>
                                    );
                                },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay:  NoRowsOverlay,
                            }}
                            rows={userData}
                            columns={(() => {
                                const p = getProfile('usersroles');
                                let c = userColumns;

                                if (p.userColumns && p.userColumns.length) {
                                    c = p.userColumns.map(v => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return null;
                                    });
                                }

                                return c;
                            })()}
                            onRowClick={handleSelectUser}
                            disableMultipleSelection
                            loading={loading}
                            disableColumnMenu
                            disableColumnFilter
                            disableVirtualization
                            sortModel={userSortModel}
                            onSortModelChange={handleUpdateUserSort}
                            onColumnResize={handleUserColumnResize}
                            onColumnOrderChange={handleUserColumnReorder}
                        />
                    </Box>}
                    {userRoleTab === 1 && <Box value={userRoleTab} index={1} height={height - 310} width="100%">
                        <DataGridPro
                            apiRef={roleTableAPIRef}
                            components={{
                                Header: !(rolesPerms.write || rolesPerms.delete) ? undefined : () => {
                                    return (
                                        <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                            {rolesPerms.write && <IconButton
                                                disabled={!selectedRole}
                                                variant="contained"
                                                color="primary"
                                                name="edit role"
                                                aria-label="edit role"
                                                title="edit role"
                                                onClick={handleEditRole}
                                                size="large"><EditIcon/></IconButton>}
                                            {rolesPerms.delete && <IconButton
                                                disabled={!selectedRole || (selectedRole && selectedRole.user_sample && Boolean(selectedRole.user_sample.length))}
                                                variant="contained"
                                                color="secondary"
                                                name="delete role"
                                                aria-label="delete role"
                                                title="delete role"
                                                onClick={handleOpenDeleteRole}
                                                size="large"><DeleteIcon/></IconButton>}
                                        </Box>
                                    );
                                },
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay:  NoRowsOverlay,
                            }}
                            rows={roleData}
                            columns={(() => {
                                const p = getProfile('usersroles');
                                let c = roleColumns;

                                if (p.roleColumns && p.roleColumns.length) {
                                    c = p.roleColumns.map(v => {
                                        for (let i = c.length; i--;) {
                                            if (v === c[i].headerName) {
                                                return c[i];
                                            }
                                        }
                                        return null;
                                    });
                                }

                                return c;
                            })()}
                            onRowClick={handleSelectRole}
                            disableMultipleSelection
                            loading={loading}
                            disableColumnMenu
                            disableColumnFilter
                            disableVirtualization
                            sortModel={roleSortModel}
                            onSortModelChange={handleUpdateRoleSort}
                            onColumnResize={handleRoleColumnResize}
                            onColumnOrderChange={handleRoleColumnReorder}
                        />
                    </Box>}
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteUserDialog}
                onOK={handleDeleteUser}
                open={deleteUserDialog}
                message={
                    <div>
                        <div>
                            Are you sure you want to remove the user {selectedUser ? selectedUser.name : ''}?
                        </div>
                        <div>
                            <FormControlLabel
                                label={`Keep dashboards and transfer to ${user}`}
                                control={ <Checkbox checked={txfDashboards} onChange={e => setTxfDashboards(e.target.checked)}/> }
                            />
                        </div>
                    </div>
                }
                messagee={`Are you sure you want to remove the user ${selectedUser ? selectedUser.name : ''}?`}
                title="Delete User"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleCloseDeleteRoleDialog}
                onOK={handleDeleteRole}
                open={deleteRoleDialog}
                message={`Are you sure you want to remove the role ${selectedRole ? selectedRole.name : ''}?`}
                title="Delete Role"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleClosePasswordResetDialog}
                onOK={handlePasswordReset}
                open={passwordResetDialog}
                message={
                    `User ${selectedUser ? selectedUser.username : ''} (${selectedUser ? selectedUser.name : ''})
                    will receive an email with instructions to complete the password reset process.
                    Email will be sent to ${selectedUser ? selectedUser.email : ''}`
                }
                title="Send Reset Email"
                cancelLabel="Cancel"
                okLabel="Send Reset Email"
            />
        </React.Fragment>
    );
};

export default Users;
